// Control hierarchy (CONTRACT "Controls"): primary filled light once per view,
// secondary outlined, tertiary text, icon square. Heights follow density
// through --spacing-control and --spacing-control-sm; lg is the 40 px
// full-width primary of the action panel only. keyCap prints a key cap after
// the label. Hover is gated to pointer devices in Button.css.
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cx } from './cx';
import { KeyCap } from './KeyCap';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'icon';
export type ButtonSize = 'md' | 'sm' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly keyCap?: string | undefined;
  // Phosphor icon element; the only content of an icon button.
  readonly icon?: ReactNode;
  // Icon buttons: draw the outline. Others: fill the row (action panel primary).
  readonly framed?: boolean;
  readonly full?: boolean;
  readonly children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', keyCap, icon, framed = false, full = false, className, children, type = 'button', ...rest },
  ref,
) {
  if (variant === 'icon' && !rest['aria-label'] && import.meta.env.DEV) {
    console.warn('[Button] icon buttons need an aria-label');
  }
  return (
    <button
      ref={ref}
      type={type}
      className={cx('btn', `btn-${variant}`, `btn-${size}`, framed && 'btn-framed', full && 'btn-full', className)}
      {...rest}
    >
      {icon ? <span className="btn-ic" aria-hidden="true">{icon}</span> : null}
      {variant === 'icon' ? null : children ? <span className="btn-lbl">{children}</span> : null}
      {keyCap ? <KeyCap className="btn-kc">{keyCap}</KeyCap> : null}
    </button>
  );
});

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly variant?: Exclude<ButtonVariant, 'icon'>;
  readonly size?: ButtonSize;
  readonly keyCap?: string | undefined;
  readonly icon?: ReactNode;
  readonly full?: boolean;
  readonly children: ReactNode;
}

// A link drawn as a button: the same classes as Button so the two never
// drift. For links out (Axiom, DEX Screener) and honest-state href actions.
export function ButtonLink({ variant = 'secondary', size = 'md', keyCap, icon, full = false, className, children, ...rest }: ButtonLinkProps) {
  return (
    <a className={cx('btn', `btn-${variant}`, `btn-${size}`, full && 'btn-full', className)} {...rest}>
      {icon ? <span className="btn-ic" aria-hidden="true">{icon}</span> : null}
      <span className="btn-lbl">{children}</span>
      {keyCap ? <KeyCap className="btn-kc">{keyCap}</KeyCap> : null}
    </a>
  );
}
